import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info, Trash2, UserX } from 'lucide-react';
import { ResponsiveModal, ResponsiveButton } from './ResponsiveModal';
import { membersApi } from '../services/api';
import toast from 'react-hot-toast';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  onSuccess: () => void;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  onSuccess
}) => {
  const [deleteType, setDeleteType] = useState<'soft' | 'hard'>('soft');

  const { data: deleteInfo, isLoading } = useQuery({
    queryKey: ['member-delete-info', memberId],
    queryFn: () => membersApi.getDeleteInfo(memberId),
    enabled: isOpen && !!memberId
  });

  const softDeleteMutation = useMutation({
    mutationFn: () => membersApi.softDelete(memberId),
    onSuccess: () => {
      toast.success('Member deactivated successfully');
      onSuccess();
      onClose();
    },
    onError: () => {
      toast.error('Failed to deactivate member');
    }
  });

  const hardDeleteMutation = useMutation({
    mutationFn: () => membersApi.delete(memberId),
    onSuccess: () => {
      toast.success('Member deleted permanently');
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  });

  const handleConfirm = () => {
    if (deleteType === 'soft') {
      softDeleteMutation.mutate();
    } else {
      hardDeleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <ResponsiveModal isOpen={isOpen} onClose={onClose} title="Loading...">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Member"
      size="md"
      footer={
        <>
          <ResponsiveButton
            variant="secondary"
            onClick={onClose}
            fullWidth
            className="sm:w-auto"
          >
            Cancel
          </ResponsiveButton>
          <ResponsiveButton
            variant="danger"
            onClick={handleConfirm}
            disabled={softDeleteMutation.isPending || hardDeleteMutation.isPending}
            fullWidth
            className="sm:w-auto sm:ml-3"
          >
            {softDeleteMutation.isPending || hardDeleteMutation.isPending 
              ? 'Processing...' 
              : deleteType === 'soft' ? 'Deactivate Member' : 'Delete Permanently'
            }
          </ResponsiveButton>
        </>
      }
    >
      <div className="space-y-6">
        {/* Member Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Member: {memberName}</h4>
          {deleteInfo && (
            <div className="space-y-1 text-sm text-gray-600">
              <p>Status: {deleteInfo.is_active ? 'Active' : 'Inactive'}</p>
              <p>Activity records: {deleteInfo.activity_count}</p>
              <p>Membership cycles: {deleteInfo.cycle_count}</p>
              <p>Token transfers: {deleteInfo.transfer_count}</p>
            </div>
          )}
        </div>

        {/* Constraint Warning */}
        {deleteInfo?.has_related_data && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Member has related data
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>This member has {deleteInfo.activity_count} activity records and other related data.</p>
                  <p className="mt-1">
                    <strong>Recommendation:</strong> Use "Deactivate" to preserve data integrity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Type Selection */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Choose deletion type:</h4>
          
          {/* Soft Delete Option */}
          <label className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="deleteType"
              value="soft"
              checked={deleteType === 'soft'}
              onChange={(e) => setDeleteType(e.target.value as 'soft')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <div className="ml-3">
              <div className="flex items-center">
                <UserX className="h-4 w-4 text-orange-500 mr-2" />
                <span className="font-medium text-gray-900">Deactivate Member (Recommended)</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Sets the member as inactive but preserves all data. Member can be reactivated later.
              </p>
            </div>
          </label>

          {/* Hard Delete Option */}
          <label className={`flex items-start p-4 border rounded-lg cursor-pointer ${
            deleteInfo?.can_hard_delete 
              ? 'border-gray-200 hover:bg-gray-50' 
              : 'border-red-200 bg-red-50 cursor-not-allowed'
          }`}>
            <input
              type="radio"
              name="deleteType"
              value="hard"
              checked={deleteType === 'hard'}
              onChange={(e) => setDeleteType(e.target.value as 'hard')}
              disabled={!deleteInfo?.can_hard_delete}
              className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 disabled:opacity-50"
            />
            <div className="ml-3">
              <div className="flex items-center">
                <Trash2 className={`h-4 w-4 mr-2 ${deleteInfo?.can_hard_delete ? 'text-red-500' : 'text-gray-400'}`} />
                <span className={`font-medium ${deleteInfo?.can_hard_delete ? 'text-gray-900' : 'text-gray-500'}`}>
                  Delete Permanently
                </span>
              </div>
              <p className={`text-sm mt-1 ${deleteInfo?.can_hard_delete ? 'text-gray-600' : 'text-red-600'}`}>
                {deleteInfo?.can_hard_delete 
                  ? 'Completely removes the member and all associated data. This action cannot be undone.'
                  : 'Cannot delete permanently because member has activity records or other related data.'
                }
              </p>
            </div>
          </label>
        </div>

        {/* Final Warning */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <Info className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {deleteType === 'soft' ? 'Deactivation Warning' : 'Permanent Deletion Warning'}
              </h3>
              <p className="mt-2 text-sm text-red-700">
                {deleteType === 'soft' 
                  ? 'The member will be marked as inactive and will not appear in active member lists. You can reactivate them later by editing the member.'
                  : 'This action is irreversible. All member data will be permanently deleted from the system.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
};